import { useEffect, useState } from "react";
import LayoutMain from "src/shared/layouts/LayoutMain/LayoutMain";
import { BCButton } from "src/shared/styled/global.styled";
import { FormDynamic } from "src/shared/interface/form-dynamic";
import { useForm } from "react-hook-form";
import SwitchFields from "src/shared/components/Bc-SwitchFields/Bc-SwitchFields";
import { FormBase } from "src/shared/interface/form-base";
import { Api } from "src/core/services/api.services";
import { Brief } from "src/shared/interface/brief";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormCampanas, FormInitiative, FormTactic } from "src/constants/form";
import { v4 as uuidv4 } from "uuid";
import BcLoading from "src/shared/components/Bc-Loading/Bc-Loading";
import { StatusBrief } from "src/shared/enums/status-brief.enum";
import BcModal from "src/shared/components/Bc-Modal/Bc-Modal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TypeBrief } from "src/shared/enums/type-brief.enum";
import { optionsTime } from "src/constants/options-time";

const briefsCreate = async (payload: Brief): Promise<Brief> => {
  const { data } = await Api.Brief().then((api) => api.create<Brief>(payload));
  return data;
};

const uploadFile = async (file: any, path: string): Promise<any> => {
  const data = await Api.Brief().then((api) => api.uploadFile(file, path));
  return data;
};

const getBriefId = async (id: string) => {
  const { data } = await Api.Brief().then((api) => api.getId<Brief>(id));
  return data;
};

const BriefForm = () => {
  const [form, setForm] = useState<FormDynamic>(FormCampanas);
  const [title, setTitle] = useState<string>("");
  const [briefType, setBriefType] = useState<string>("");
  const [openDiag, setOpenDiag] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [idCreate, setIdCreate] = useState<string>("");
  const [nameCreate, setNameCreate] = useState<string>("");

  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    unregister,
    setValue,
    handleSubmit,
    getValues,
    watch,
    reset,
    resetField,
    formState: { errors, touchedFields, dirtyFields, isValid },
  } = useForm({ mode: "onChange" });
  const onSubmit = handleSubmit(async (data: any) => {
    setLoading(true);
    const { username } = JSON.parse(localStorage.getItem("user") as string);

    const path = uuidv4().replace(/-/g, "");

    const newBrief = {
      name: `Brief de ${data.name}`,
      user: username,
      version: 1,
      dateCreate: new Date().toLocaleString("es-ES", optionsTime as any),
      dateEdit: new Date().toLocaleString("es-ES", optionsTime as any),
      briefType: briefType,
      fields: data,
      status: StatusBrief.COMPLETE,
    };

    await uploadFile(newBrief.fields.preread[0], path).then(
      async (r: any) => (newBrief.fields.preread = await r.photoUrl)
    );
    await uploadFile(newBrief.fields.onepage[0], path).then(
      async (r: any) => (newBrief.fields.onepage = await r.photoUrl)
    );
    mutation.mutate(newBrief);
  });

  const { type, id } = useParams();

  const serviceQuery = useQuery({
    queryKey: ["brief", id],
    queryFn: () => getBriefId(id as string),
    enabled: id !== undefined,
  });

  useEffect(() => {
    if (id) {
      console.log(id);
      if (serviceQuery.isSuccess) {
        const fields = serviceQuery.data.fields;

        Object.keys(fields).forEach((item: any) => {
          setValue(item, fields[item]);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (location.pathname.startsWith("/brief/crear")) {
      reset();
    }
  }, [location, reset]);

  const handleModal = () => {
    setOpenDiag(!openDiag);
  };

  useEffect(() => {
    switch (type) {
      case "campaign":
        setForm(FormCampanas);
        setTitle("Campaña");
        setBriefType(TypeBrief.CAMPANA);
        break;
      case "iniciative":
        setForm(FormInitiative);
        setTitle("Iniciativa");
        setBriefType(TypeBrief.INICIATIVA);
        break;
      case "tactic":
        setForm(FormTactic);
        setTitle("Tactico");
        setBriefType(TypeBrief.TACTICA);
        break;
    }
  }, [type]);

  const mutation = useMutation(briefsCreate, {
    onSuccess: (data: Brief) => {
      handleModal();
      setLoading(false);
      setIdCreate(data.id as string);
      setNameCreate(data.name as string);
    },
  });

  return (
    <LayoutMain
      idPage={`Brief ${title}`}
      titlePage={true}
      className="main-center"
    >
      <section className="bc-container bc-card">
        <form onSubmit={onSubmit}>
          {form?.fields &&
            form.fields.map((field: FormBase) => (
              <SwitchFields
                key={`master${field.item}`}
                field={field}
                errors={errors}
                register={register}
                unregister={unregister}
                setValue={setValue}
                isRequired={field.isRequired}
                getValues={getValues}
                watch={watch}
                resetField={resetField}
                isValid={isValid}
                touchedFields={touchedFields}
                dirtyFields={dirtyFields}
              />
            ))}
          <BCButton
            type="button"
            className="bc-button-primary bc-button-small"
            disabled={!isValid || mutation.isLoading}
            onClick={handleModal}
          >
            Enviar
          </BCButton>
        </form>
        {mutation.isLoading && <BcLoading messages="Brief" />}
      </section>
      <BcModal
        openModal={openDiag}
        type="info"
        title="Crear formulario"
        onClose={handleModal}
      >
        <section className="bc-modal-content">
          <div className="text-center bc-mb-2 bc-mt-2">
            <p>Estas a punto de crear un formulario dinamico,</p>
            <p>
              <strong>¿Deseas continuar?</strong>
            </p>
          </div>
        </section>
        <section className="bc-modal-button-container">
          <BCButton
            type="button"
            className="bc-button-primary bc-button-small"
            disabled={loading}
            tabIndex={0}
            onClick={onSubmit}
          >
            Continuar
          </BCButton>
          <BCButton
            type="button"
            className="bc-button-secondary bc-button-small"
            tabIndex={1}
            onClick={handleModal}
          >
            Cancelar
          </BCButton>
        </section>
      </BcModal>
      <BcModal
        openModal={mutation.isSuccess}
        type="success"
        title="Creación exitosa"
        onClose={handleModal}
      >
        <section className="bc-modal-content">
          <div className="text-center bc-mb-2 bc-mt-2">
            <p>
              Brief con nombre: {nameCreate} y id: {idCreate} fue creado con
              exito.
            </p>
          </div>
        </section>
        <section className="bc-modal-button-container">
          <BCButton
            type="button"
            className="bc-button-primary bc-button-small"
            tabIndex={0}
            onClick={() => navigate(`/brief/ver/${idCreate}`)}
          >
            Aceptar
          </BCButton>
        </section>
      </BcModal>
      <BcModal
        openModal={mutation.isError}
        type="error"
        title="Error "
        onClose={handleModal}
      >
        <section className="bc-modal-content">
          <div className="text-center bc-mb-2 bc-mt-2">
            <p>{JSON.stringify(mutation.error)}</p>
          </div>
        </section>
        <section className="bc-modal-button-container">
          <BCButton
            type="button"
            className="bc-button-primary bc-button-small"
            tabIndex={0}
            onClick={handleModal}
          >
            Aceptar
          </BCButton>
        </section>
      </BcModal>
    </LayoutMain>
  );
};

export default BriefForm;
